import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export default function HomePage() {
  return (
    <div className="bg-white" style={{ fontFamily: 'Inter, -apple-system, sans-serif' }}>
      {/* Hero Section */}
      <section className="min-h-[90vh] flex items-center bg-gradient-to-b from-[#FAF9F7] to-white">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-20 md:py-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="inline-block bg-[#F5F3F0] text-[#A8923F] px-4 py-2 rounded-full text-sm font-medium mb-6 border border-[#E8E4DE]">
                Kompetenzzentrum für menschliche Intelligenz
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-[52px] font-bold text-gray-900 leading-[1.1] tracking-tight mb-6">
                Weil der Mensch<br />den Menschen braucht.
              </h1>
              <p className="text-xl md:text-[22px] font-medium text-[#A8923F] mb-6 tracking-tight">
                Potenziale sehen. Möglichkeiten schaffen. Zukunft gestalten.
              </p>
              <p className="text-[17px] text-gray-600 mb-8 leading-relaxed max-w-lg">
                asK-mi unterstützt junge Menschen ab 14 Jahren aus dem neurodiversen Spektrum 
                und schafft Möglichkeiten für Entwicklung, Teilhabe und Zukunft.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/was-wir-tun"
                  className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-7 py-3.5 rounded-md font-medium transition-colors"
                >
                  Mehr erfahren
                </Link>
                <Link 
                  to="/foerderer-werden"
                  className="inline-flex items-center gap-2 border border-[#E8E4DE] hover:border-[#C4A962] text-gray-900 hover:text-[#A8923F] px-7 py-3.5 rounded-md font-medium transition-colors"
                >
                  Förderer werden
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/d4ed44b3-63d8-417d-957c-4836bdf688a4/images/b3e0acb007c65051fb3d7ef36316d75491187f3510504cb98c37f5a494589038.jpeg" 
                alt="Mentoring und Begegnung"
                className="w-full h-[400px] md:h-[500px] object-cover rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-5 -right-5 md:bottom-6 md:right-6 bg-white p-5 rounded-xl shadow-lg border border-gray-100">
                <p className="font-semibold text-gray-900">Gemeinsam wachsen</p>
                <p className="text-sm text-gray-500">Verbindung · Entwicklung · Zukunft</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Warum wir da sind */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-semibold text-[#C4A962] uppercase tracking-[2px] mb-4 block">
                Warum wir da sind
              </span>
              <blockquote className="text-2xl md:text-[32px] font-medium text-gray-900 leading-snug border-l-4 border-[#C4A962] pl-8 mb-8">
                „Viele junge Menschen werden an dem gemessen, was ihnen schwerfällt."
              </blockquote>
              <div className="text-[17px] text-gray-600 leading-relaxed space-y-4">
                <p>
                  ADS, ADHS oder Autismus beschreiben einen Teil eines Menschen.
                </p>
                <p className="font-semibold text-gray-900">
                  Sie definieren nicht seinen Wert.
                </p>
                <p>
                  asK-mi möchte den Blick auf Fähigkeiten, Potenziale und Möglichkeiten richten – 
                  und dazu beitragen, dass diese erkannt, entwickelt und in konkrete Chancen übersetzt werden.
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/d4ed44b3-63d8-417d-957c-4836bdf688a4/images/e626f660b056b38749e8c7ed4a4700de984a7f7c9e2488bc5977f3a2b14208fe.jpeg" 
                alt="Menschen in Begegnung"
                className="w-full h-[450px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Wen wir fördern */}
      <section className="py-24 md:py-32 bg-[#FAF9F7]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 text-center">
          <span className="text-xs font-semibold text-[#C4A962] uppercase tracking-[2px] mb-4 block">
            Wen wir fördern
          </span>
          <h2 className="text-3xl md:text-[42px] font-bold text-gray-900 tracking-tight mb-6">
            Junge Menschen ab 14 Jahren
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Menschen aus dem neurodiversen Spektrum – mit ADS, ADHS oder aus dem Autismus-Spektrum.
          </p>
          <p className="text-xl md:text-2xl font-medium text-[#A8923F] max-w-2xl mx-auto leading-relaxed">
            „Nicht die Diagnose steht im Mittelpunkt.<br />Der Mensch steht im Mittelpunkt."
          </p>
        </div>
      </section>

      {/* Bildwelt */}
      <section className="py-16">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-xl overflow-hidden h-[280px]">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/d4ed44b3-63d8-417d-957c-4836bdf688a4/images/3052d869b03defd54e88eceb682182670c0a74a913ed846c04d644a63bbd40c0.jpeg" 
                alt="Gemeinsames Lernen"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-xl overflow-hidden h-[280px]">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/d4ed44b3-63d8-417d-957c-4836bdf688a4/images/ef7a66be3918896115991a12b0f92a84b8619c07d92b5250238f05079156e853.jpeg" 
                alt="Perspektive und Potenzial"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-xl overflow-hidden h-[280px]">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/d4ed44b3-63d8-417d-957c-4836bdf688a4/images/d15332ffe234a8dde0c9445d896152f835808b8c432a1283e3fe4c7dd6198c7e.jpeg" 
                alt="Familie und Unterstützung"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Fördermodell */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold text-[#C4A962] uppercase tracking-[2px] mb-4 block">
              So funktioniert Förderung
            </span>
            <h2 className="text-3xl md:text-[42px] font-bold text-gray-900 tracking-tight mb-6">
              Förderung, die ankommt.
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              asK-mi bringt Förderer und Menschen zusammen – und ermöglicht gezielte, wirkungsvolle Unterstützung.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
            <div className="text-center">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-[#F5F3F0] border-2 border-[#E8E4DE] flex items-center justify-center mx-auto mb-4 text-3xl hover:border-[#C4A962] transition-colors">
                🤝
              </div>
              <p className="font-semibold text-gray-900">Förderer</p>
              <p className="text-sm text-gray-500">Menschen & Unternehmen</p>
            </div>
            <div className="text-2xl text-[#C4A962] hidden md:block">→</div>
            <div className="text-center">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-[#C4A962] flex items-center justify-center mx-auto mb-4">
                <img src="/images/brand/ask-mi-logo-clean.webp" alt="asK-mi" className="h-14 w-auto" />
              </div>
              <p className="font-semibold text-gray-900">asK-mi</p>
              <p className="text-sm text-gray-500">Prüfung & Auswahl</p>
            </div>
            <div className="text-2xl text-[#C4A962] hidden md:block">→</div>
            <div className="text-center">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-[#F5F3F0] border-2 border-[#E8E4DE] flex items-center justify-center mx-auto mb-4 text-3xl hover:border-[#C4A962] transition-colors">
                ✓
              </div>
              <p className="font-semibold text-gray-900">Förderung</p>
              <p className="text-sm text-gray-500">Gezielt & transparent</p>
            </div>
            <div className="text-2xl text-[#C4A962] hidden md:block">→</div>
            <div className="text-center">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-[#F5F3F0] border-2 border-[#E8E4DE] flex items-center justify-center mx-auto mb-4 text-3xl hover:border-[#C4A962] transition-colors">
                💡
              </div>
              <p className="font-semibold text-gray-900">Wirkung</p>
              <p className="text-sm text-gray-500">Konkrete Veränderung</p>
            </div>
          </div>
        </div>
      </section>

      {/* Wen wir fördern - Erweitertes Förder-Ökosystem */}
      <section className="py-24 md:py-32 bg-[#FAF9F7]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold text-[#C4A962] uppercase tracking-[2px] mb-4 block">
              Wen wir fördern
            </span>
            <h2 className="text-3xl md:text-[42px] font-bold text-gray-900 tracking-tight mb-6">
              Menschen und Strukturen stärken.
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              asK-mi unterstützt nicht nur einzelne junge Menschen, sondern auch die Menschen und Strukturen, 
              die ihnen Halt, Orientierung und Entwicklungsmöglichkeiten geben.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: '🧑‍🤝‍🧑', title: 'Jugendliche & junge Erwachsene', text: 'Menschen ab 14 Jahren aus dem neurodiversen Spektrum.' },
              { icon: '👨‍👩‍👧', title: 'Familien & Angehörige', text: 'Menschen, die Jugendliche und junge Erwachsene begleiten und unterstützen.' },
              { icon: '👥', title: 'Selbsthilfegruppen', text: 'Gruppen, in denen Betroffene, Angehörige und Familien Erfahrungen teilen und sich gegenseitig stärken.', link: '/selbsthilfe' },
              { icon: '🏛️', title: 'Einrichtungen', text: 'Organisationen, die konkrete Unterstützungs- und Entwicklungsangebote schaffen.' },
              { icon: '🏢', title: 'Unternehmen', text: 'Unternehmen, die jungen Menschen Perspektiven, Praktika, Ausbildung und berufliche Entwicklung ermöglichen.' },
              { icon: '🚀', title: 'Projekte & Forschung', text: 'Vorhaben, die neue Wege für Neurodiversität, Teilhabe und Entwicklung eröffnen.' },
            ].map((item, i) => (
              <div 
                key={i}
                className="bg-white border border-[#E8E4DE] rounded-2xl p-8 hover:border-[#C4A962] hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
              >
                <div className="text-4xl mb-5">{item.icon}</div>
                <h3 className="font-semibold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
                {item.link && (
                  <Link to={item.link} className="text-[#A8923F] text-sm font-medium hover:underline mt-3 inline-block">
                    Mehr erfahren →
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wirkung */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold text-[#C4A962] uppercase tracking-[2px] mb-4 block">
              Wirkung
            </span>
            <h2 className="text-3xl md:text-[42px] font-bold text-gray-900 tracking-tight">
              Was aus Förderung entstehen kann.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { text: 'Ein Jugendlicher findet einen passenden Ausbildungsplatz.', label: 'Perspektive' },
              { text: 'Eine Familie erhält die Unterstützung, die sie braucht.', label: 'Entlastung' },
              { text: 'Eine Einrichtung kann ein neues Angebot schaffen.', label: 'Entwicklung' },
              { text: 'Ein Unternehmen öffnet eine neue Perspektive.', label: 'Chance' },
              { text: 'Ein Projekt kann endlich umgesetzt werden.', label: 'Umsetzung' },
              { text: 'Neue Forschung kann entstehen und wirken.', label: 'Erkenntnis' },
            ].map((item, i) => (
              <div 
                key={i}
                className="bg-[#F5F3F0] rounded-xl p-8 border-l-4 border-[#C4A962] hover:bg-white hover:shadow-md transition-all duration-300"
              >
                <p className="text-gray-900 italic leading-relaxed mb-4">„{item.text}"</p>
                <p className="text-xs font-semibold text-[#A8923F] uppercase tracking-wider">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Förderer & Unterstützer */}
      <section className="py-24 md:py-32 bg-[#FAF9F7]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold text-[#C4A962] uppercase tracking-[2px] mb-4 block">
              Förderer & Unterstützer
            </span>
            <h2 className="text-3xl md:text-[42px] font-bold text-gray-900 tracking-tight">
              Menschen und Unternehmen,<br />die Verantwortung übernehmen.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'liQro Systems', company: null, role: 'Förderer der asK-mi' },
              { name: 'Felix Bender', company: null, role: 'Unterstützer der asK-mi' },
              { name: 'Thorsten Reiser', company: 'anticipater', role: 'Unterstützer der asK-mi', link: 'https://anticipater.com' },
              { name: 'Dr. Reinhard Zinburg', company: 'Heilpraxis Cenith', role: 'Unterstützer & Sponsor der asK-mi' },
              { name: 'Andreas Nader', company: 'Nader Consulting', role: 'Unterstützer der asK-mi' },
            ].map((item, i) => (
              <div 
                key={i}
                className="bg-white rounded-xl p-7 border border-[#E8E4DE] hover:border-[#C4A962] transition-colors"
              >
                <p className="font-semibold text-gray-900 text-lg">{item.name}</p>
                {item.company && (
                  <p className="text-[#A8923F] text-sm mt-1">
                    {item.link ? (
                      <a href={item.link} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {item.company}
                      </a>
                    ) : item.company}
                  </p>
                )}
                <p className="text-gray-500 text-sm mt-2">{item.role}</p>
              </div>
            ))}
            <div className="bg-[#F5F3F0] rounded-xl p-7 border border-dashed border-[#E8E4DE] flex flex-col justify-center">
              <p className="font-semibold text-gray-400 text-lg">Ihr Unternehmen?</p>
              <Link to="/foerderer-werden" className="text-[#A8923F] text-sm mt-2 hover:underline flex items-center gap-1">
                Werden Sie Förderer <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-gray-900 text-white text-center">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <h2 className="text-3xl md:text-[48px] font-bold tracking-tight mb-6 leading-tight">
            Potenzial erkennen.<br />Zukunft ermöglichen.
          </h2>
          <p className="text-lg text-white/70 max-w-xl mx-auto mb-10 leading-relaxed">
            Werden Sie Teil einer Bewegung, die junge Menschen nicht über ihre Schwierigkeiten definiert, 
            sondern über ihre Möglichkeiten.
          </p>
          <Link 
            to="/foerderer-werden"
            className="inline-flex items-center gap-2 bg-[#C4A962] hover:bg-[#D4B972] text-gray-900 px-9 py-4 rounded-md font-semibold text-lg transition-all hover:-translate-y-0.5"
          >
            Förderer werden
          </Link>
        </div>
      </section>
    </div>
  );
}
